/**
 * The current selection's actions, published once and read by every surface that shows them: the
 * inspector footer, the status line, ⌘K and the key handler (docs/ux/artboards/optimal decision
 * 6). Whatever owns the selection (a decision view, the inspector frame) calls
 * `useSelectionActions`; the newest live owner wins, so a nested inspector never fights the page.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { type Action, actionForKey } from './actions.js'
import { isTyping } from './shortcut.js'

export interface Selection {
  /** "Snail Mail: ship the cache" — what the actions apply to. */
  title: string
  actions: readonly Action[]
  /** The inspector key of the selected entity (`decision:<id>`), when it has one. */
  key?: string
}

const owners = new Map<string, Selection>()
const listeners = new Set<() => void>()
let snapshot: Selection | undefined

function publish() {
  snapshot = [...owners.values()].at(-1)
  for (const l of listeners) l()
}

export function setSelection(owner: string, sel: Selection | undefined) {
  if (sel) {
    owners.delete(owner) // newest publisher wins
    owners.set(owner, sel)
  } else if (!owners.delete(owner)) return
  publish()
}

export const currentSelection = (): Selection | undefined => snapshot

export function useSelection(): Selection | undefined {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    currentSelection,
    currentSelection,
  )
}

/** Publish `actions` for `owner` while mounted. */
export function useSelectionActions(
  owner: string,
  title: string,
  actions: readonly Action[],
  key?: string,
) {
  useEffect(() => {
    setSelection(owner, actions.length ? { title, actions, ...(key ? { key } : {}) } : undefined)
  }, [owner, title, actions, key])
  useEffect(() => () => setSelection(owner, undefined), [owner])
}

/**
 * Single-key handler for a list of actions. `only` limits it to the ids a surface owns (a list
 * may already own `r`/`d`/`e`); disabled actions swallow nothing so the key falls through.
 */
export function useActionKeys(
  list: readonly Action[],
  o: { enabled?: boolean; only?: (a: Action) => boolean; capture?: boolean } = {},
) {
  const latest = useRef({ list, only: o.only })
  latest.current = { list, only: o.only }
  const on = o.enabled !== false
  useEffect(() => {
    if (!on) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || isTyping(e)) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      const { list: l, only } = latest.current
      const a = actionForKey(
        l.filter((x) => !x.disabledReason && (!only || only(x))),
        e,
      )
      if (!a) return
      e.preventDefault()
      e.stopImmediatePropagation()
      a.run()
    }
    addEventListener('keydown', onKey, o.capture !== false)
    return () => removeEventListener('keydown', onKey, o.capture !== false)
  }, [on, o.capture])
}
